import React, { useContext } from 'react';
import { PropTypes } from 'prop-types';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import useSessionStore from '../../../hooks/useSessionStore';
import ExternalCoachContext from '../../../CoachTools/context/ExternalCoachContext';
import ChatChannelContext from '../../context/ChatChannelContext';
import MessageInputContext from '../../context/MessageInputContext';
import ChatMessage from '../../Model/ChatMessage';
import User from '../../../Model/User';
import { ChannelType } from '../../Model/ChatChannel';
import { ModalType } from '../MessagePreview/constants';
import { FeatureFlag, useClientHighlightSharingEnabled } from '../../../utils/postHog';
import { getReplyBlockReason } from '../../utils/replyBlock';
import texts from './texts.json';

import {
  ActionButton,
  Container,
} from './styles';

const MessageActions = ({
  closeActionMenu,
  isOwnMessage,
  setOpenModal,
  message,
  clientUserDoc,
}) => {
  const { setReply } = useContext(MessageInputContext);
  const { isAdmin, isCoachAssistant, isCoachOrAdmin } = useSessionStore();
  const { activeChannel } = useContext(ChatChannelContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const isCoachTasksEnabled = useFeatureFlagEnabled(FeatureFlag.ENABLE_COACH_TASKS);
  const isHighlightSharingEnabled = useClientHighlightSharingEnabled(activeChannel?.coachId);
  const canUseHighlightSharing = (isAdmin || isCoachAssistant) && isCoachTasksEnabled && isHighlightSharingEnabled;
  const isAdminCoaching = activeChannel?.type === ChannelType.ADMIN_COACHING;

  // Only internal staff (CAM/AC/admin) assign coach tasks; coaches don't self-assign. Also require a
  // resolved client↔coach pair — without it CreateCoachTaskModal renders null, so the button would be
  // a silent no-op (e.g. group channels have no userId/coachId).
  const hasClientCoachPair = !!activeChannel?.userId
    && !!activeChannel?.coachId
    && activeChannel.userId !== activeChannel.coachId;
  const canCreateTask = (isAdmin || isCoachAssistant) && hasClientCoachPair && isCoachTasksEnabled;
  const showFlagAction = !message.isFlagged && !activeChannel?.isAIFriendEnabled;
  const isClientMessage = !!activeChannel?.userId && message.userId === activeChannel.userId;
  const canShareAsHighlight = canUseHighlightSharing
    && hasClientCoachPair
    && isClientMessage
    && !message.isSystem
    && !!message.text?.trim();

  // Unflagging is deliberately only offered when replying is impossible: a coach or AC who can
  // still answer the client is expected to do so, which clears the flag server-side. For a
  // canceled, unpaid or paused client no reply is ever coming, so without this the 24h Slack
  // re-ping chain would run forever.
  const isReplyBlocked = !!getReplyBlockReason({ isCoachOrAdmin, clientUserDoc, coachDoc });
  const showUnflagAction = message.isFlagged && isReplyBlocked;

  const openModal = (type) => (event) => {
    event.stopPropagation();
    closeActionMenu();
    setOpenModal(type);
  };

  const handleSetReply = (event) => {
    event.stopPropagation();
    setReply(message, closeActionMenu);
  };

  return (
    <Container>
      <ActionButton onClick={isAdminCoaching ? openModal(ModalType.THREAD) : handleSetReply}>
        {isAdminCoaching ? texts.replyInThread : texts.reply}
      </ActionButton>
      {canCreateTask && (
        <ActionButton onClick={openModal(ModalType.CREATE_COACH_TASK)}>
          {texts.createTask}
        </ActionButton>
      )}
      {canShareAsHighlight && (
        <ActionButton onClick={openModal(ModalType.SHARE_AS_HIGHLIGHT)}>
          {texts.shareAsHighlight}
        </ActionButton>
      )}
      {isOwnMessage && (
        <ActionButton onClick={openModal(ModalType.EDIT)}>
          {texts.edit}
        </ActionButton>
      )}
      {isOwnMessage && (
        <ActionButton onClick={openModal(ModalType.DELETE)}>
          {texts.delete}
        </ActionButton>
      )}
      {showFlagAction && (
        <ActionButton onClick={openModal(ModalType.FLAG)}>
          {texts.flag}
        </ActionButton>
      )}
      {showUnflagAction && (
        <ActionButton onClick={openModal(ModalType.UNFLAG)}>
          {texts.unflag}
        </ActionButton>
      )}
    </Container>
  );
};

MessageActions.propTypes = {
  isOwnMessage: PropTypes.bool,
  closeActionMenu: PropTypes.func.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  message: PropTypes.instanceOf(ChatMessage).isRequired,
  clientUserDoc: PropTypes.instanceOf(User),
};

MessageActions.defaultProps = {
  isOwnMessage: true,
  clientUserDoc: null,
};

export default MessageActions;
